/* eslint-disable react-native/no-inline-styles */
/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { Alert, Space, Spin } from 'antd'
import React, { useMemo } from 'react'
import banners from '../../Form/banner-helpers'
import { Separator } from '../GeneralForm'
import { useScheduleContext } from '../ScheduleContext'
import '../styles.scss'
import Text from 'src/components/ts-components/Text'
import { TextInput } from 'src/components/ts-components/TextInput'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { captionLarge } from 'src/components/css'
import PhoneInput from 'src/components/ts-components/PhoneInput'

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

type ContactInfoFormProps = {
  onCancelPressed: () => void
  triggerRefresh: () => void
}

const ContactInfoForm: React.FC<ContactInfoFormProps> = ({
  onCancelPressed,
  triggerRefresh,
}) => {
  const {
    isEdit,
    error,
    setError,
    isLoading,
    onSubmit,
    onCancel,
    customError,
    fieldErrors,
    setFieldErrors,
    setCustomError,
    contactInfo,
    setContactInfo,
  } = useScheduleContext()

  const invalidContactInfo = useMemo(() => {
    return (
      fieldErrors.includes('phone') ||
      fieldErrors.includes('email') ||
      fieldErrors.includes('website') ||
      fieldErrors.includes('support_phone') ||
      fieldErrors.includes('support_email') ||
      fieldErrors.includes('support_website')
    )
  }, [fieldErrors])

  return (
    <div
      css={css`
        padding-top: 16px;
        padding-left: 16px;
        padding-right: 16px;
        display: flex;
        flex: 1;
        flex-direction: column;
        height: 100%;
        overflow-y: auto;
        max-height: 550px;
        gap: 16px;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          gap: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <Text variant='heading5' weight='semiBold' color={colors.NEUTRAL_0}>
              Contact Info
            </Text>
          </div>
        </div>
        <div
          css={css`
            display: flex;
            align-items: flex-start;
            align-self: stretch;
            flex-direction: row;
            gap: 16px;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
            `}
          >
            <PhoneInput
              value={contactInfo?.phone || ''}
              onChange={(value) => {
                setContactInfo({ ...contactInfo, phone: value })
              }}
              label='Phone Number'
              error={
                fieldErrors.includes('phone') ? 'Invalid phone number' : ''
              }
            />
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
            `}
          >
            <TextInput
              label='Email'
              value={contactInfo?.email || ''}
              setValue={(value) => {
                setContactInfo({ ...contactInfo, email: value })
              }}
              onBlur={() => {
                if (
                  contactInfo.email &&
                  contactInfo.email.length > 0 &&
                  !emailRegex.test(contactInfo.email)
                ) {
                  setFieldErrors([...fieldErrors, 'email'])
                  setCustomError({
                    ...customError,
                    email: 'Invalid email',
                  })
                } else {
                  setFieldErrors(
                    fieldErrors.filter((field) => field !== 'email')
                  )
                  setCustomError({
                    ...customError,
                    email: '',
                  })
                }
              }}
              fieldErrors={fieldErrors}
              customError={customError}
              disabled={false}
              name='email'
              type='email'
            />
          </div>
        </div>
        <div
          css={css`
            display: flex;
            align-items: flex-start;
            align-self: stretch;
            flex-direction: row;
            gap: 16px;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
            `}
          >
            <TextInput
              label='Website'
              value={contactInfo?.website || ''}
              setValue={(value) => {
                setContactInfo({ ...contactInfo, website: value })
              }}
              onBlur={() => {
                const regex =
                  /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/
                if (
                  contactInfo.website &&
                  contactInfo.website.length > 0 &&
                  !contactInfo.website.match(regex)
                ) {
                  setFieldErrors([...fieldErrors, 'website'])
                  setCustomError({
                    ...customError,
                    website: 'Invalid website',
                  })
                } else {
                  // remove the field from the fieldErrors and customError
                  setFieldErrors(
                    fieldErrors.filter((field) => field !== 'website')
                  )
                  setCustomError({
                    ...customError,
                    website: '',
                  })
                }
              }}
              fieldErrors={fieldErrors}
              customError={customError}
              disabled={false}
              name='website'
              type='url'
            />
          </div>
        </div>
        <Separator />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            gap: 16px;
            align-self: stretch;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
            `}
          >
            <Text variant='heading5' weight='semiBold' color={colors.NEUTRAL_0}>
              Support Info
            </Text>
          </div>
        </div>
        <div
          css={css`
            display: flex;
            align-items: flex-start;
            align-self: stretch;
            flex-direction: row;
            gap: 16px;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
            `}
          >
            <PhoneInput
              value={contactInfo?.support_phone || ''}
              onChange={(value) => {
                setContactInfo({ ...contactInfo, support_phone: value })
              }}
              label='Support Phone'
              error={
                fieldErrors.includes('support_phone')
                  ? 'Invalid phone number'
                  : ''
              }
            />
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
            `}
          >
            <TextInput
              label='Support Email'
              value={contactInfo?.support_email || ''}
              setValue={(value) => {
                setContactInfo({ ...contactInfo, support_email: value })
              }}
              onBlur={() => {
                if (
                  contactInfo.support_email &&
                  contactInfo.support_email.length > 0 &&
                  !emailRegex.test(contactInfo.support_email)
                ) {
                  setFieldErrors([...fieldErrors, 'support_email'])
                  setCustomError({
                    ...customError,
                    support_email: 'Invalid Support Email',
                  })
                } else {
                  setFieldErrors(
                    fieldErrors.filter((field) => field !== 'support_email')
                  )
                  setCustomError({
                    ...customError,
                    support_email: '',
                  })
                }
              }}
              fieldErrors={fieldErrors}
              customError={customError}
              disabled={false}
              name='support_email'
              type='email'
            />
          </div>
        </div>
        <div
          css={css`
            display: flex;
            align-items: flex-start;
            align-self: stretch;
            flex-direction: row;
            gap: 16px;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              flex: 1 0 0;
            `}
          >
            <TextInput
              label='Support Website'
              value={contactInfo?.support_website || ''}
              setValue={(value) => {
                setContactInfo({ ...contactInfo, support_website: value })
              }}
              onBlur={() => {
                const regex =
                  /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/
                if (
                  contactInfo.support_website &&
                  contactInfo.support_website.length > 0 &&
                  !contactInfo.support_website.match(regex)
                ) {
                  setFieldErrors([...fieldErrors, 'support_website'])
                  setCustomError({
                    ...customError,
                    support_website: 'Invalid Support Website',
                  })
                } else {
                  // remove the field from the fieldErrors and customError
                  setFieldErrors(
                    fieldErrors.filter((field) => field !== 'support_website')
                  )
                  setCustomError({
                    ...customError,
                    support_website: '',
                  })
                }
              }}
              fieldErrors={fieldErrors}
              customError={customError}
              disabled={false}
              name='support_website'
              type='url'
            />
          </div>
        </div>
      </div>
      <div
        css={css`
          position: fixed;
          bottom: 20px;
          right: 38px;
          width: 200px;
          background-color: ${colors.HEADER};
          z-index: 100;
          padding-bottom: 16px;
          padding-top: 16px;
        `}
      >
        {error ? (
          <Space
            direction='vertical'
            style={{ width: '100%', marginBottom: 8 }}
          >
            <Alert
              message={error || 'An error occurred. Please try again.'}
              type='error'
              showIcon
              closable
            />
          </Space>
        ) : null}
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 16px;
            align-self: stretch;
          `}
        >
          {/* Cancel Button */}
          <div
            css={css`
              max-width: 80px;
              display: flex;
              padding: 8px 24px;
              justify-content: center;
              align-items: center;
              gap: 8px;
              flex: 1 0 0;
              border-radius: 8px;
              border: 1px solid var(--Neutrals-neutrals-0, #fff);
              cursor: ${isLoading ? 'not-allowed' : 'pointer'};
              opacity: ${isLoading ? 0.7 : 1};
            `}
            onClick={() => {
              if (isLoading) {
                return
              }
              // Clear the state
              onCancel()
              //   and close the modal
              onCancelPressed()
            }}
          >
            <p
              css={css`
                ${captionLarge}
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                color: ${colors.WHITE};
              `}
            >
              Cancel
            </p>
          </div>
          <div
            css={css`
              max-width: 80px;
              display: flex;
              padding: 8px 24px;
              justify-content: center;
              align-items: center;
              gap: 8px;
              flex: 1 0 0;
              border-radius: 8px;
              background: ${colors.PRIMARY};
              cursor: ${isLoading || invalidContactInfo
                ? 'not-allowed'
                : 'pointer'};
              opacity: ${isLoading || invalidContactInfo ? 0.7 : 1};
            `}
            onClick={async () => {
              try {
                if (isLoading || invalidContactInfo) {
                  return
                }
                const response = await onSubmit()
                if (isEdit) {
                  // @ts-ignore
                  banners.entityUpdate(ENTITY_TYPES.schedule)(response)
                } else {
                  banners.entityCreate(ENTITY_TYPES.schedule)(response)
                }
                triggerRefresh()
                onCancelPressed()
                onCancel()
              } catch (e: any) {
                const errorMessage = getErrorMessage(e)
                if (e?.invalid_fields) {
                  if (
                    e?.invalid_fields?.name ||
                    e?.invalid_fields?.abbreviation
                  ) {
                    setError(errorMessage)
                  }
                }
              }
            }}
          >
            <>
              {isLoading ? (
                <Spin
                  size='small'
                  css={css`
                    color: ${colors.WHITE};
                  `}
                />
              ) : (
                <p
                  css={css`
                    ${captionLarge}
                    font-family: ${isCanlan
                      ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                      : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                    color: ${colors.WHITE};
                  `}
                >
                  Save
                </p>
              )}
            </>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContactInfoForm
